import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { RunSourcePicker } from "@/pages/tina/runs/components/run-source-picker";
import {
  CONTENT_TYPES,
  type ContentType,
  SOURCE_KINDS,
  type SourceKind,
  type TinaSiteOption,
} from "@/pages/tina/runs/components/site-picker/utils";
import { useRunPickerSites } from "@/pages/tina/runs/hooks/use-run-picker-sites";
import {
  type CityFacet,
  useSiteFacets,
} from "@/pages/tina/sites/hooks/use-site-facets";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export interface GroupSitePickerProps {
  /** Site NAME strings — the group's `sites` field. */
  selected: string[];
  onChange: (next: string[]) => void;
}

/**
 * Site picker for the group form. Mirrors the runs/create picker controller
 * (same scope state, `useRunPickerSites` + `useSiteFacets` wiring, source-kind
 * mapping, `knownSites` accumulation, scope-reset effects) but — unlike
 * runs/create, which buckets selection per `comboKey(content×source×country)`
 * so it does NOT accumulate across countries — keeps ONE FLAT selection (the
 * group's `sites` list) regardless of which country/content/source is being
 * browsed. Switching scope only changes the browse list, never the selection.
 */
export function GroupSitePicker({ selected, onChange }: GroupSitePickerProps) {
  const [contentType, setContentType] = useState<ContentType>("music_event");
  const [source, setSource] = useState<SourceKind>("websites");
  const [country, setCountry] = useState<string | null>(null);
  const [city, setCity] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [knownSites, setKnownSites] = useState<Map<string, TinaSiteOption>>(
    () => new Map(),
  );

  // The picker's "websites" source maps to the backend `source_kind=web`;
  // "instagram"/"facebook" pass through as their own source_kind groups.
  const sourceKind =
    source === "instagram"
      ? "instagram"
      : source === "facebook"
        ? "facebook"
        : "web";

  // Country + city options come from /sites/facets (no counts), scoped to enabled
  // sites in the active content type / source. The city list holds every city
  // with its country, so a city can be searched without picking a country.
  const facets = useSiteFacets({
    contentType,
    sourceKind,
    enabled: true,
  });
  const countryOptions = facets.countries;
  const cityOptions = facets.cityCountries;

  // Debounce the search input so we don't refetch on every keystroke.
  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedQuery(query),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [query]);

  const { rows, total, isLoading, error, fetchAllInScope } = useRunPickerSites({
    contentType,
    sourceKind,
    countryCode: country,
    city,
    query: debouncedQuery,
    page,
    pageSize: PAGE_SIZE,
  });

  // Accumulate the records we've seen (pages + select-all) so the basket can
  // show a city for any selected site, even ones not on the current page —
  // across ALL scopes, since the selection itself is flat/scope-independent.
  useEffect(() => {
    if (rows.length === 0) return;
    setKnownSites((prev) => {
      const next = new Map(prev);
      for (const site of rows) next.set(site.name, site);
      return next;
    });
  }, [rows]);

  // Keep the selected country valid for the active content × source.
  useEffect(() => {
    if (countryOptions.length === 0) {
      setCountry((current) => (current === null ? current : null));
      return;
    }
    setCountry((current) =>
      current && countryOptions.includes(current) ? current : countryOptions[0],
    );
  }, [countryOptions]);

  // City + search are refinements of the current scope — reset on scope change.
  // A country change is handled in `changeCountry`/`pickCity`, not here, so
  // picking a city can set its country without wiping that city.
  useEffect(() => {
    setCity(null);
    setQuery("");
  }, [contentType, source]);

  const changeCountry = useCallback((next: string) => {
    setCountry(next);
    setCity(null);
    setQuery("");
  }, []);

  const pickCity = useCallback((pick: CityFacet | null) => {
    setQuery("");
    if (pick === null) {
      setCity(null);
      return;
    }
    setCountry(pick.country_code);
    setCity(pick.city);
  }, []);

  // Any scope/search change returns to the first page.
  useEffect(() => {
    setPage(1);
  }, [contentType, source, country, city, debouncedQuery]);

  // Flat selection: the group's `sites` list, not partitioned by comboKey.
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const onToggle = useCallback(
    (name: string) =>
      onChange(
        selectedSet.has(name)
          ? selected.filter((current) => current !== name)
          : [...selected, name],
      ),
    [onChange, selected, selectedSet],
  );
  const onSelect = useCallback(
    (names: string[]) => onChange(Array.from(new Set([...selected, ...names]))),
    [onChange, selected],
  );
  const onDeselect = useCallback(
    (names: string[]) => {
      const remove = new Set(names);
      onChange(selected.filter((name) => !remove.has(name)));
    },
    [onChange, selected],
  );
  const onClear = useCallback(() => onChange([]), [onChange]);

  // "Select all in country" fetches every name in the current content × source ×
  // country scope (server-side, paged) and adds them to the flat selection.
  const onSelectAllInScope = useCallback(async () => {
    const all = await fetchAllInScope();
    if (all.length === 0) return;
    setKnownSites((prev) => {
      const next = new Map(prev);
      for (const site of all) next.set(site.name, site);
      return next;
    });
    onSelect(all.map((site) => site.name));
  }, [fetchAllInScope, onSelect]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          value={contentType}
          onValueChange={(value) => setContentType(value as ContentType)}
        >
          <TabsList>
            {CONTENT_TYPES.map((option) => (
              <TabsTrigger key={option.value} value={option.value}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="inline-flex overflow-hidden rounded-md border">
          {SOURCE_KINDS.map((option, index) => (
            <Button
              key={option.value}
              type="button"
              variant={source === option.value ? "secondary" : "ghost"}
              size="sm"
              className={cn(
                "rounded-none",
                index > 0 && "border-l",
                source === option.value && "font-semibold",
              )}
              onClick={() => setSource(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <RunSourcePicker
        rows={rows}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        isLoading={isLoading}
        error={error}
        contentType={contentType}
        source={source}
        country={country}
        countryOptions={countryOptions}
        onCountryChange={changeCountry}
        city={city}
        onCityChange={pickCity}
        cityOptions={cityOptions}
        query={query}
        onQueryChange={setQuery}
        selectedNames={selected}
        selected={selectedSet}
        knownSites={knownSites}
        onToggle={onToggle}
        onSelect={onSelect}
        onSelectAllInScope={onSelectAllInScope}
        onDeselect={onDeselect}
        onClear={onClear}
      />
    </div>
  );
}
